import { useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { toast } from 'sonner'
import {
  Plus, Trash2, Search, ExternalLink, Pencil, Check, X, Loader2, Tag, FolderOpen,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Group {
  id: string
  agent_connection_id: string
  group_name: string
  group_url: string
  group_fb_id: string | null
  tags: string[] | null
  default_media_folder_ids: string[] | null
  enabled: boolean
  connection: {
    id: string
    fb_page_name: string
    agent_profile_id: string
  } | null
}

interface UniqueGroup {
  url: string
  name: string
  tags: string[]
  defaultFolderIds: string[]
  ids: string[]
  pages: string[]
  pageCount: number
}

interface MediaFolder {
  id: string
  name: string
  images_count: number
}

/* ------------------------------------------------------------------ */
/*  Tag input                                                          */
/* ------------------------------------------------------------------ */

function TagEditor({ tags, allTags, onChange }: { tags: string[]; allTags: string[]; onChange: (tags: string[]) => void }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')

  const available = useMemo(() => {
    const list = allTags.filter(t => !tags.includes(t))
    if (!q) return list
    return list.filter(t => t.toLowerCase().includes(q.toLowerCase()))
  }, [allTags, tags, q])

  const add = (tag: string) => {
    const t = tag.trim().toLowerCase()
    if (t && !tags.includes(t)) onChange([...tags, t])
  }

  const remove = (tag: string) => onChange(tags.filter(t => t !== tag))

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1">
        {tags.map(t => (
          <span key={t} className="flex items-center gap-0.5 rounded-full bg-indigo-600/20 px-2 py-0.5 text-[10px] text-indigo-300">
            {t}
            <button onClick={() => remove(t)} className="ml-0.5 text-indigo-400 hover:text-white"><X className="h-2.5 w-2.5" /></button>
          </span>
        ))}
        <button
          onClick={() => { setOpen(!open); setQ('') }}
          className="rounded-full border border-dashed border-zinc-600 px-1.5 py-0.5 text-[10px] text-zinc-500 hover:border-indigo-500 hover:text-indigo-400"
        >
          + tag
        </button>
      </div>
      {open && (
        <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-1.5 space-y-1">
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && q.trim()) { add(q); setQ(''); setOpen(false) }
              if (e.key === 'Escape') setOpen(false)
            }}
            placeholder="Buscar ou criar tag..."
            className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-300 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
          />
          <div className="max-h-24 overflow-y-auto space-y-0.5">
            {available.map(t => (
              <button
                key={t}
                onClick={() => { add(t); setQ(''); setOpen(false) }}
                className="w-full rounded px-2 py-0.5 text-left text-[10px] text-zinc-300 hover:bg-zinc-800"
              >
                {t}
              </button>
            ))}
            {q.trim() && !allTags.includes(q.trim().toLowerCase()) && (
              <button
                onClick={() => { add(q); setQ(''); setOpen(false) }}
                className="w-full rounded px-2 py-0.5 text-left text-[10px] text-emerald-400 hover:bg-zinc-800"
              >
                + Criar "{q.trim().toLowerCase()}"
              </button>
            )}
            {available.length === 0 && !q.trim() && (
              <div className="text-[10px] text-zinc-600 px-2 py-1">Todas as tags ja atribuidas</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Multi folder picker                                                */
/* ------------------------------------------------------------------ */

function MultiFolderPicker({ selected, folders, onChange }: {
  selected: string[]
  folders: MediaFolder[]
  onChange: (ids: string[]) => void
}) {
  const [q, setQ] = useState('')

  const filtered = useMemo(() => {
    if (!q) return folders
    const lower = q.toLowerCase()
    return folders.filter(f => f.name.toLowerCase().includes(lower))
  }, [folders, q])

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id])
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1">
        {selected.map(id => {
          const f = folders.find(x => x.id === id)
          return f ? (
            <span key={id} className="flex items-center gap-0.5 rounded-full bg-emerald-600/20 px-2 py-0.5 text-[10px] text-emerald-300">
              {f.name}
              <button onClick={() => toggle(id)} className="ml-0.5 text-emerald-400 hover:text-white"><X className="h-2.5 w-2.5" /></button>
            </span>
          ) : null
        })}
      </div>
      <input
        value={q}
        onChange={e => setQ(e.target.value)}
        placeholder="Buscar pasta..."
        className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-300 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
      />
      {(q || selected.length === 0) && (
        <div className="max-h-28 overflow-y-auto space-y-0.5">
          {filtered.slice(0, 20).map(f => (
            <label key={f.id} className="flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[10px] text-zinc-300 hover:bg-zinc-800 cursor-pointer">
              <input type="checkbox" checked={selected.includes(f.id)} onChange={() => toggle(f.id)} className="rounded" />
              <span className="truncate">{f.name}</span>
              <span className="ml-auto text-zinc-600">({f.images_count})</span>
            </label>
          ))}
          {filtered.length === 0 && <div className="text-[10px] text-zinc-600 px-1.5 py-1">Nenhuma pasta encontrada</div>}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Bulk folder assignment by tag                                      */
/* ------------------------------------------------------------------ */

function TagFolderPanel({ tag, groups, folders, onApply, applying }: {
  tag: string
  groups: UniqueGroup[]
  folders: MediaFolder[]
  onApply: (folderIds: string[]) => void
  applying: boolean
}) {
  // Start with the most common folders across groups in this tag
  const commonFolders = useMemo(() => {
    const counts = new Map<string, number>()
    for (const g of groups) {
      for (const fid of g.defaultFolderIds) {
        counts.set(fid, (counts.get(fid) || 0) + 1)
      }
    }
    // Return folders used by at least 1 group
    return Array.from(counts.keys())
  }, [groups])

  const [selected, setSelected] = useState<string[]>(commonFolders)

  // Sync when tag changes
  const [lastTag, setLastTag] = useState(tag)
  if (tag !== lastTag) {
    setSelected(commonFolders)
    setLastTag(tag)
  }

  const withoutFolders = groups.filter(g => g.defaultFolderIds.length === 0).length
  const changed = JSON.stringify(selected.sort()) !== JSON.stringify(commonFolders.sort())

  return (
    <div className="rounded-xl border border-indigo-600/30 bg-indigo-950/20 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <FolderOpen className="h-3.5 w-3.5 text-indigo-400" />
        <span className="text-xs font-medium text-indigo-300">
          Pastas da tag "{tag}"
        </span>
        <span className="text-[10px] text-zinc-500">
          — {groups.length} grupo(s){withoutFolders > 0 && `, ${withoutFolders} sem pasta`}
        </span>
      </div>
      <div className="max-w-md">
        <MultiFolderPicker selected={selected} folders={folders} onChange={setSelected} />
      </div>
      {selected.length > 0 && (
        <button
          onClick={() => onApply(selected)}
          disabled={applying}
          className="rounded-lg bg-indigo-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
        >
          {applying
            ? <Loader2 className="h-3 w-3 animate-spin inline" />
            : `Aplicar ${selected.length} pasta(s) nos ${groups.length} grupos`
          }
        </button>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Editable row                                                       */
/* ------------------------------------------------------------------ */

function GroupRow({ group, folders, allTags, onUpdateName, onUpdateTags, onUpdateFolders, onDelete }: {
  group: UniqueGroup
  folders: MediaFolder[]
  allTags: string[]
  onUpdateName: (ids: string[], name: string) => void
  onUpdateTags: (ids: string[], tags: string[]) => void
  onUpdateFolders: (ids: string[], folderIds: string[]) => void
  onDelete: (ids: string[]) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(group.name)

  const slug = group.url.replace(/^https?:\/\/(www\.)?facebook\.com/, '')
  const isNumeric = /^\d+$/.test(group.name.trim())

  const save = () => {
    const trimmed = draft.trim()
    if (trimmed && trimmed !== group.name) {
      onUpdateName(group.ids, trimmed)
    }
    setEditing(false)
  }

  return (
    <tr className="border-b border-zinc-800/30 hover:bg-zinc-800/20 transition-colors">
      {/* Name */}
      <td className="py-2 pl-4 pr-2">
        {editing ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
              className="flex-1 rounded border border-indigo-500 bg-zinc-900 px-2 py-0.5 text-xs text-zinc-200 focus:outline-none"
            />
            <button onClick={save} className="rounded p-0.5 text-emerald-400 hover:bg-emerald-500/10"><Check className="h-3.5 w-3.5" /></button>
            <button onClick={() => setEditing(false)} className="rounded p-0.5 text-zinc-500 hover:bg-zinc-700"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`truncate ${isNumeric ? 'text-amber-400' : 'text-zinc-200'}`} title={isNumeric ? 'Nome e apenas ID — clique no lapiz para renomear' : group.name}>
              {group.name}
            </span>
            <button onClick={() => { setDraft(group.name); setEditing(true) }} className="rounded p-0.5 text-zinc-600 hover:text-zinc-300 shrink-0" title="Editar nome">
              <Pencil className="h-3 w-3" />
            </button>
          </div>
        )}
      </td>

      {/* URL */}
      <td className="py-2 px-2">
        <div className="flex items-center gap-1 min-w-0">
          <span className="truncate text-xs text-zinc-500" title={group.url}>{slug}</span>
          <a href={group.url} target="_blank" rel="noopener" className="text-zinc-600 hover:text-indigo-400 shrink-0">
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        </div>
      </td>

      {/* Tags */}
      <td className="py-2 px-2">
        <TagEditor tags={group.tags} allTags={allTags} onChange={tags => onUpdateTags(group.ids, tags)} />
      </td>

      {/* Media folders */}
      <td className="py-2 px-2">
        <MultiFolderPicker
          selected={group.defaultFolderIds}
          folders={folders}
          onChange={ids => onUpdateFolders(group.ids, ids)}
        />
      </td>

      {/* Pages using */}
      <td className="py-2 px-2">
        <span className="text-xs text-zinc-400" title={group.pages.join(', ')}>
          {group.pageCount} {group.pageCount === 1 ? 'pag.' : 'pags.'}
        </span>
      </td>

      {/* Actions */}
      <td className="py-2 px-2 text-center">
        <button
          onClick={() => {
            if (confirm(`Remover "${group.name}" de todas as ${group.pageCount} pagina(s)?`))
              onDelete(group.ids)
          }}
          className="rounded p-1 text-zinc-600 hover:bg-red-500/10 hover:text-red-400"
          title="Remover de todas as paginas"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  )
}

/* ------------------------------------------------------------------ */
/*  Add group form                                                     */
/* ------------------------------------------------------------------ */

function AddGroupForm({ allTags, onDone }: { allTags: string[]; onDone: () => void }) {
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const qc = useQueryClient()
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    const trimUrl = url.trim()
    const trimName = name.trim()
    if (!trimUrl) { toast.error('URL obrigatoria'); return }
    if (!trimName) { toast.error('Nome obrigatorio'); return }
    if (!trimUrl.includes('facebook.com/groups/')) { toast.error('URL precisa ser de um grupo do Facebook'); return }

    setSaving(true)
    try {
      const connsData = await api.get<{ connections: { id: string }[] }>('/automacao-fb/connections')
      const conns = connsData?.connections ?? []
      if (conns.length === 0) {
        toast.error('Nenhuma pagina cadastrada — cadastre uma pagina primeiro')
        setSaving(false)
        return
      }

      const res = await api.post<{ ok: boolean; group: { id: string } }>('/automacao-fb/page-groups', {
        connection_id: conns[0].id,
        group_name: trimName,
        group_url: trimUrl,
      })

      if (tags.length > 0 && res?.group?.id) {
        await api.put(`/automacao-fb/page-groups/${res.group.id}`, { tags })
      }

      toast.success(`Grupo "${trimName}" cadastrado`)
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-groups'] })
      setName('')
      setUrl('')
      setTags([])
      onDone()
    } catch {
      toast.error('Erro ao cadastrar grupo')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-800/40 p-4 space-y-3">
      <h3 className="text-xs font-semibold text-zinc-300">Novo grupo</h3>
      <div className="grid grid-cols-[1fr_1.5fr] gap-3">
        <div className="space-y-1">
          <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Nome</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Ex.: Investidores Brasil"
            className="w-full rounded-lg border border-zinc-700 bg-zinc-900/50 px-3 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
          />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Link do grupo</label>
          <input
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://facebook.com/groups/..."
            className="w-full rounded-lg border border-zinc-700 bg-zinc-900/50 px-3 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
          />
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider">Tags</label>
        <div className="rounded-lg border border-zinc-700 bg-zinc-900/50 px-3 py-1.5">
          <TagEditor tags={tags} allTags={allTags} onChange={setTags} />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={saving || !name.trim() || !url.trim()}
          className="rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Cadastrar'}
        </button>
        <button onClick={onDone} className="rounded-lg px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200">
          Cancelar
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export function GroupsCatalogTab() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState(false)
  const [filter, setFilter] = useState<'all' | 'unnamed'>('all')
  const [tagFilter, setTagFilter] = useState<string | null>(null)

  const { data: groupsRaw, isLoading } = useQuery<{ groups: Group[] }>({
    queryKey: ['automacao-fb', 'all-groups'],
    queryFn: () => api.get('/automacao-fb/page-groups'),
    refetchInterval: 30000,
  })

  const { data: foldersRaw } = useQuery<any[]>({
    queryKey: ['media-folders'],
    queryFn: () => api.get('/media-folders'),
  })

  const groups: Group[] = groupsRaw?.groups ?? []
  const folders: MediaFolder[] = useMemo(() => {
    const raw = Array.isArray(foldersRaw) ? foldersRaw : []
    return raw.map((f: any) => ({
      id: f.id,
      name: f.name,
      images_count: f.images?.length ?? f.images_count ?? 0,
    }))
  }, [foldersRaw])

  // All unique tags across groups
  const allTags = useMemo(() => {
    const set = new Set<string>()
    for (const g of groups) {
      for (const t of g.tags ?? []) set.add(t)
    }
    return Array.from(set).sort()
  }, [groups])

  // Deduplicate by URL → unique groups
  const uniqueGroups = useMemo(() => {
    const map = new Map<string, UniqueGroup>()
    for (const g of groups) {
      const existing = map.get(g.group_url)
      if (existing) {
        existing.ids.push(g.id)
        const pageName = g.connection?.fb_page_name ?? '?'
        if (!existing.pages.includes(pageName)) existing.pages.push(pageName)
        existing.pageCount = existing.pages.length
        // Merge tags
        for (const t of g.tags ?? []) {
          if (!existing.tags.includes(t)) existing.tags.push(t)
        }
        // Merge default folder ids
        for (const fid of g.default_media_folder_ids ?? []) {
          if (!existing.defaultFolderIds.includes(fid)) existing.defaultFolderIds.push(fid)
        }
      } else {
        map.set(g.group_url, {
          url: g.group_url,
          name: g.group_name,
          tags: [...(g.tags ?? [])],
          defaultFolderIds: [...(g.default_media_folder_ids ?? [])],
          ids: [g.id],
          pages: [g.connection?.fb_page_name ?? '?'],
          pageCount: 1,
        })
      }
    }

    let list = Array.from(map.values())

    if (filter === 'unnamed') {
      list = list.filter(g => /^\d+$/.test(g.name.trim()))
    }

    if (tagFilter) {
      list = list.filter(g => g.tags.includes(tagFilter))
    }

    if (search) {
      // Busca robusta: ignora emoji/acento/pontuação e casa por PALAVRAS (ordem livre).
      // Antes era substring contíguo — nomes com emoji no meio (ex.: "Pakita 🍑 BR 153")
      // faziam a busca pelo texto puro falhar e o grupo "sumia" da tag.
      const strip = (str: string) => (str || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')  // remove acentos
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')                   // remove emoji/pontuação
        .replace(/\s+/g, ' ').trim().toLowerCase()
      const words = strip(search).split(' ').filter(Boolean)
      if (words.length) {
        list = list.filter(g => {
          const hay = strip([g.name, g.url, ...g.pages, ...g.tags].join(' '))
          return words.every(w => hay.includes(w))
        })
      }
    }

    return list.sort((a, b) => a.name.localeCompare(b.name))
  }, [groups, search, filter, tagFilter])

  const totalUnique = useMemo(() => {
    const urls = new Set(groups.map(g => g.group_url))
    return urls.size
  }, [groups])

  const unnamedCount = useMemo(() => {
    const seen = new Set<string>()
    let count = 0
    for (const g of groups) {
      if (seen.has(g.group_url)) continue
      seen.add(g.group_url)
      if (/^\d+$/.test(g.group_name.trim())) count++
    }
    return count
  }, [groups])

  const updateName = useMutation({
    mutationFn: async ({ ids, name }: { ids: string[]; name: string }) => {
      for (const id of ids) await api.put(`/automacao-fb/page-groups/${id}`, { group_name: name })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-groups'] }); toast.success('Nome atualizado') },
    onError: () => toast.error('Erro ao atualizar'),
  })

  const updateTags = useMutation({
    mutationFn: async ({ ids, tags }: { ids: string[]; tags: string[] }) => {
      for (const id of ids) await api.put(`/automacao-fb/page-groups/${id}`, { tags })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-groups'] }); toast.success('Tags atualizadas') },
    onError: () => toast.error('Erro ao atualizar tags'),
  })

  const deleteAll = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await api.delete(`/automacao-fb/page-groups/${id}`)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-groups'] }); toast.success('Grupo removido') },
    onError: () => toast.error('Erro ao remover'),
  })

  const updateFolders = useMutation({
    mutationFn: async ({ ids, folderIds }: { ids: string[]; folderIds: string[] }) => {
      for (const id of ids) await api.put(`/automacao-fb/page-groups/${id}`, { default_media_folder_ids: folderIds })
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-groups'] }); toast.success('Pastas atualizadas') },
    onError: () => toast.error('Erro ao atualizar pastas'),
  })

  const handleUpdateName = useCallback((ids: string[], name: string) => updateName.mutate({ ids, name }), [updateName])
  const handleUpdateTags = useCallback((ids: string[], tags: string[]) => updateTags.mutate({ ids, tags }), [updateTags])
  const handleUpdateFolders = useCallback((ids: string[], folderIds: string[]) => updateFolders.mutate({ ids, folderIds }), [updateFolders])
  const handleDelete = useCallback((ids: string[]) => deleteAll.mutate(ids), [deleteAll])

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar grupo por nome, URL, pagina ou tag..."
            className="w-full rounded-lg border border-zinc-700 bg-zinc-800/50 py-1.5 pl-8 pr-3 text-xs text-zinc-200 placeholder:text-zinc-500 focus:border-indigo-500 focus:outline-none"
          />
        </div>

        <div className="flex rounded-lg border border-zinc-700 overflow-hidden shrink-0">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 text-[11px] ${filter === 'all' ? 'bg-zinc-700 text-zinc-200' : 'text-zinc-500 hover:text-zinc-300'}`}
          >
            Todos ({totalUnique})
          </button>
          <button
            onClick={() => setFilter('unnamed')}
            className={`px-3 py-1 text-[11px] ${filter === 'unnamed' ? 'bg-amber-600/30 text-amber-400' : 'text-zinc-500 hover:text-zinc-300'}`}
          >
            Sem nome ({unnamedCount})
          </button>
        </div>

        <button
          onClick={() => setAdding(!adding)}
          className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 shrink-0"
        >
          <Plus className="h-3.5 w-3.5" /> Novo grupo
        </button>
      </div>

      {/* Tag filter pills */}
      {allTags.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <Tag className="h-3 w-3 text-zinc-600 shrink-0" />
          <button
            onClick={() => setTagFilter(null)}
            className={`rounded-full px-2.5 py-0.5 text-[10px] transition-colors ${!tagFilter ? 'bg-indigo-600/30 text-indigo-300' : 'border border-zinc-700 text-zinc-500 hover:text-zinc-300'}`}
          >
            Todas
          </button>
          {allTags.map(t => (
            <button
              key={t}
              onClick={() => setTagFilter(tagFilter === t ? null : t)}
              className={`rounded-full px-2.5 py-0.5 text-[10px] transition-colors ${tagFilter === t ? 'bg-indigo-600/30 text-indigo-300' : 'border border-zinc-700 text-zinc-500 hover:text-zinc-300'}`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {/* Bulk folder assignment by tag */}
      {tagFilter && uniqueGroups.length > 0 && (
        <TagFolderPanel
          tag={tagFilter}
          groups={uniqueGroups}
          folders={folders}
          onApply={(folderIds) => {
            const allIds = uniqueGroups.flatMap(g => g.ids)
            updateFolders.mutate({ ids: allIds, folderIds })
          }}
          applying={updateFolders.isPending}
        />
      )}

      {/* Add form */}
      {adding && <AddGroupForm allTags={allTags} onDone={() => setAdding(false)} />}

      {/* Unnamed warning */}
      {unnamedCount > 0 && filter !== 'unnamed' && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-600/30 bg-amber-950/30 px-3 py-2">
          <span className="text-[11px] text-amber-400">
            {unnamedCount} grupo(s) com ID numerico no lugar do nome.
          </span>
          <button onClick={() => setFilter('unnamed')} className="text-[11px] text-amber-300 underline hover:text-amber-200">
            Filtrar e corrigir
          </button>
        </div>
      )}

      {/* Table */}
      {uniqueGroups.length === 0 ? (
        <div className="py-12 text-center text-xs text-zinc-500">
          {search || tagFilter ? 'Nenhum grupo encontrado' : 'Nenhum grupo cadastrado'}
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-800 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-800/40 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="py-2 pl-4 pr-2 text-left font-medium w-[22%]">Nome</th>
                <th className="py-2 px-2 text-left font-medium w-[20%]">Link</th>
                <th className="py-2 px-2 text-left font-medium w-[18%]">Tags</th>
                <th className="py-2 px-2 text-left font-medium w-[22%]">Pastas padrao</th>
                <th className="py-2 px-2 text-left font-medium w-[10%]">Usado em</th>
                <th className="py-2 px-2 text-center font-medium w-[8%]"></th>
              </tr>
            </thead>
            <tbody>
              {uniqueGroups.map(g => (
                <GroupRow
                  key={g.url}
                  group={g}
                  folders={folders}
                  allTags={allTags}
                  onUpdateName={handleUpdateName}
                  onUpdateTags={handleUpdateTags}
                  onUpdateFolders={handleUpdateFolders}
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
